// Score one record against this model, deployed or not.
//
// Follows `leafGridApi.ts` rather than the store pattern: one page-scoped read
// with no shared state.
import axios from 'axios';

import ModelBaseNames from '../../../models/modelBase/ModelBaseNames';
import { Rule } from '../../../models/rules';

export interface ScoreLookupStep {
  nodeId: number;
  condition: string;
  passed: boolean;
  values: Record<string, string | number | null>;
  /** The node this step lands on, and how it converts. */
  reachedNodeId: number | null;
  reachedRate: number | null;
}

export interface ScoreLookupTree {
  treeId: number;
  name: string;
  path: ScoreLookupStep[];
  leafId: number | null;
  /** The leaf's conversion rate as a percentage, which is its contribution. */
  leafRate: number | null;
  weight: number;
}

export interface ScoreLookupSignal {
  group: string;
  label: string;
  direction: string;
}

/** One rule group that fired on a point based record, and what it added. */
export interface ScoreLookupRule {
  points: number;
  values: { computation: string; value: string | number | null }[];
}

export interface ScoreLookup {
  email: string;
  base: ModelBaseNames;
  trees: ScoreLookupTree[];
  rules: ScoreLookupRule[];
  ruleGroups: number;
  pointsRange: { min: number; max: number } | null;
  score: number | null;
  segment: string | null;
  scoreBeforeOverrides: number | null;
  segmentBeforeOverrides: string | null;
  overrideRuleIndex: number | null;
  /** The rule itself, so the page shows what it says rather than linking away. */
  overrideRule: Rule | null;
  overrideMovedSegment: boolean;
  thresholds: Record<string, number>;
  attributes: Record<string, string | number | null>;
  /** What this record would carry into the CRM, one per group at most. */
  signals: ScoreLookupSignal[];
}

/**
 * POST, not GET: the subject is an email address, and an address in a URL
 * lands in access logs and in browser history.
 *
 * Errors are thrown rather than swallowed. Unlike the live performance read,
 * this is the whole point of the page: a lookup that quietly returns nothing
 * is indistinguishable from a record that does not score.
 */
export async function fetchScoreLookup(
  tenant: string | number,
  modelId: string | number,
  email: string
): Promise<ScoreLookup> {
  const response = await axios.post<ScoreLookup>(
    `/api/tenant/${tenant}/model/${modelId}/score_lookup`,
    { email }
  );
  return response.data;
}
